// IndexedDB-backed upload queue. Selecting a photo writes the bytes to IDB
// immediately, so the user can navigate away, kill the tab, lose signal — the
// worker keeps trying. CLAUDE.md HARD RULE 7: assume the network is broken.
//
// iOS Safari (17/18) has a bug where writing a Blob directly into IndexedDB
// throws "Error preparing Blob/File data to be stored in object store". So we
// serialise blobs as raw ArrayBuffer + mimeType internally and reconstitute
// them as Blob on read. Callers see plain Blob shapes.
import { openDB, DBSchema, IDBPDatabase } from 'idb';

export type QueueStatus = 'pending' | 'uploading' | 'complete' | 'failed';

export interface QueueItem {
  id: string;
  eventId: string;
  slug: string;
  filename: string;
  mimeType: string;
  size: number;
  blob: Blob;          // the (frame-composited, compressed) bytes to send
  thumbBlob: Blob;     // the pre-generated thumbnail
  sessionUri?: string; // Drive resumable session URI, once minted
  bytesSent?: number;  // progress persisted across sessions
  driveSlot?: number;  // which connected drive account the mint chose
  driveFileId?: string;
  thumbPath?: string;
  status: QueueStatus;
  attempts: number;
  lastError?: string;
  uploaderName?: string;
  tableId?: string;
  createdAt: number;
}

// What actually goes into IndexedDB. Blobs replaced with ArrayBuffer + type.
interface StoredItem extends Omit<QueueItem, 'blob' | 'thumbBlob'> {
  blobBuf: ArrayBuffer;
  blobType: string;
  thumbBuf: ArrayBuffer;
  thumbType: string;
}

interface Schema extends DBSchema {
  uploads: {
    key: string;
    value: StoredItem;
    indexes: { 'by-status': QueueStatus };
  };
}

let dbP: Promise<IDBPDatabase<Schema>> | null = null;
function getDB() {
  if (!dbP) {
    // Bump to v2 so browsers with the old blob-shaped store rebuild cleanly.
    dbP = openDB<Schema>('snapweds-queue', 2, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const store = db.createObjectStore('uploads', { keyPath: 'id' });
          store.createIndex('by-status', 'status');
        } else if (oldVersion < 2) {
          // Old items stored raw Blobs which now-broken on iOS. Blow them
          // away — a lost pending upload is better than a stuck queue.
          db.deleteObjectStore('uploads');
          const store = db.createObjectStore('uploads', { keyPath: 'id' });
          store.createIndex('by-status', 'status');
        }
      },
    });
  }
  return dbP;
}

async function toStored(item: QueueItem): Promise<StoredItem> {
  const { blob, thumbBlob, ...rest } = item;
  const [blobBuf, thumbBuf] = await Promise.all([
    blob.arrayBuffer(),
    thumbBlob.arrayBuffer(),
  ]);
  return {
    ...rest,
    blobBuf,
    blobType: blob.type || item.mimeType,
    thumbBuf,
    thumbType: thumbBlob.type || 'image/jpeg',
  };
}

function fromStored(stored: StoredItem): QueueItem {
  const { blobBuf, blobType, thumbBuf, thumbType, ...rest } = stored;
  return {
    ...rest,
    blob: new Blob([blobBuf], { type: blobType }),
    thumbBlob: new Blob([thumbBuf], { type: thumbType }),
  };
}

export async function enqueue(item: QueueItem): Promise<void> {
  const db = await getDB();
  await db.put('uploads', await toStored(item));
  notify();
}

export async function updateItem(id: string, patch: Partial<QueueItem>): Promise<void> {
  const db = await getDB();
  const existing = await db.get('uploads', id);
  if (!existing) return;
  // Merge patch — but if it carries blobs, serialise them too.
  const merged: QueueItem = { ...fromStored(existing), ...patch };
  await db.put('uploads', await toStored(merged));
  notify();
}

export async function getPending(): Promise<QueueItem[]> {
  const db = await getDB();
  const idx = db.transaction('uploads').store.index('by-status');
  const pending = await idx.getAll('pending');
  const uploading = await idx.getAll('uploading');
  return [...pending, ...uploading].map(fromStored);
}

export async function listAll(): Promise<QueueItem[]> {
  const db = await getDB();
  const all = await db.getAll('uploads');
  return all.map(fromStored);
}

export async function removeItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('uploads', id);
  notify();
}

// ---- pub/sub for the UI ----
type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  for (const fn of listeners) fn();
}
